<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="mm-header">
        <span class="mm-bar mm-bar--xl"></span>
        <span class="mm-bar mm-bar--md"></span>
      </div>
      <div class="mm-scale">
        <div
          v-for="(m, i) in moods"
          :key="m.emoji"
          class="mm-col"
          :style="{ '--i': i }"
        >
          <div class="mm-stack">
            <span
              v-for="d in m.count"
              :key="d"
              class="mm-dot"
              :style="{ '--d': d - 1 }"
            ></span>
          </div>
          <span class="mm-emoji">{{ m.emoji }}</span>
        </div>
      </div>
      <div class="mm-axis"></div>
    </template>

    <template #audience>
      <span class="mm-phone-q"></span>
      <div class="mm-phone-hint">
        How do you feel?
      </div>
      <div class="mm-phone-row">
        <button
          v-for="(m, i) in moods"
          :key="m.emoji"
          type="button"
          class="mm-phone-btn"
          :class="{ '--tapped': i === tapIndex }"
          tabindex="-1"
        >
          {{ m.emoji }}
        </button>
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewMoodMeter',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      moods: [
        { emoji: '😞', count: 2 },
        { emoji: '😐', count: 4 },
        { emoji: '🙂', count: 6 },
        { emoji: '😄', count: 5 },
      ],
      tapIndex: 2,
    }
  },
})
</script>

<style scoped lang="scss">
.mm-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.mm-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); }
.mm-bar--md { width: 40%; }

.mm-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.mm-scale {
  display: grid; grid-template-columns: repeat(4, 1fr);
  align-items: end; gap: 4px;
  height: 100px;
  padding: 4px;
}
.mm-col {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  height: 100%;
  justify-content: flex-end;
}
.mm-stack {
  display: flex; flex-direction: column-reverse; gap: 3px;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
}
.mm-dot {
  width: 10px; height: 10px;
  border-radius: 999px;
  background: var(--accent);
  opacity: 0;
  animation: mm-pop 4s ease-out infinite;
  animation-delay: calc(var(--i) * 100ms + var(--d) * 200ms);
}
.mm-emoji {
  font-size: 18px;
}
.mm-axis {
  height: 1px;
  background: rgba(15, 15, 20, 0.1);
  margin-top: -4px;
}

/* phone */
.mm-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 2px;
}
.mm-phone-hint {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.mm-phone-row {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 5px;
}
.mm-phone-btn {
  appearance: none;
  height: 36px;
  font-size: 18px;
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.1);
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  display: grid; place-items: center;
}
.mm-phone-btn.--tapped {
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  background: color-mix(in srgb, var(--accent) 10%, white);
  animation: mm-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

@keyframes mm-pop {
  0%, 10%  { opacity: 0; transform: scale(0.5); }
  25%      { opacity: 1; transform: scale(1.2); }
  35%, 90% { opacity: 1; transform: scale(1); }
  100%     { opacity: 1; transform: scale(1); }
}
@keyframes mm-tap {
  0%, 4%  { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%     { box-shadow: 0 0 0 6px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%    { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .mm-dot, .mm-phone-btn.--tapped { animation: none; }
  .mm-dot { opacity: 1; transform: scale(1); }
}
</style>
